// Project Board (Phase 3) data shapes — derived from §1/§4 of
// PHASE3_PROJECTBOARD_INVENTORY.md (Part A). Re-platform of the Backbone
// project-board models; field lists follow the live API responses the Backbone
// views consume.

/**
 * THE project model (GET /api/v1.0/projects/{id} — data-layer row 3).
 * Partner role fields ({role}_co_id / {role}_contact_id for the 12 roles) are
 * reached through the index signature.
 */
export interface IProjectMain {
  id: number;
  name: string;
  editable?: boolean;
  cms_id?: number;
  user_id?: number | null;
  by_project_user?: boolean;
  is_critical_task_started?: boolean;
  is_can_set_project_logo?: boolean;
  project_state?: string;
  quote_number?: string;
  job_number?: string;
  is_custom_quote_number?: boolean;
  is_custom_job_number?: boolean;
  custom_quote_number?: string | null;
  custom_job_number?: string | null;
  create_date?: string;
  created_by?: string;
  address_line_one?: string | null;
  address_line_two?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  sccr?: number | string | null;
  elevation?: number | string | null;
  warranty?: string | null;
  submittal_cover_note?: string | null;
  submittal_cover_note_checkbox?: boolean;
  network_editing_enabled?: boolean;
  mfg_name?: string;
  market_id?: number | null;
  [key: string]: unknown;
}

/**
 * Project PDF row (GET /project/pdf/?project_id={id} —
 * KsdmProjectPDFSerializer, Phase 3f §B4). `pdf_file` is the served file URL
 * (FileField) or null before the first upload.
 */
export interface IProjectPdf {
  id: number;
  user_id?: number | null;
  file_name: string | null;
  pdf_file?: string | null;
  project_id?: number | null;
  [key: string]: unknown;
}

/**
 * Project note row (GET /project/notes/?project_id={id} —
 * KsdmProjectNotesSerializer, Phase 3f §B5). `note_text` is the RAW HTML of
 * the legacy contenteditable body (stored markup, re-injected on render).
 */
export interface IProjectNote {
  id: number;
  user_id?: number | null;
  note_name: string | null;
  note_text?: string | null;
  project_id?: number | null;
  [key: string]: unknown;
}

/** Master tab catalog row (GET /master/v1.0.0/ahu/project_board_tabs/). */
export interface IMasterBoardTab {
  id: number;
  name: string;
  value: string; // hash fragment ('project', 'costing', …)
  order: number;
  logo: string; // icon suffix → `icon-{logo}`
  /**
   * KA-5258 / PR #832 (master/models_global.py:570, migration
   * `0134_masterprojectboardtab_allow_editing`). `MasterProjectBoardTabSerializer`
   * is `fields = '__all__'` (api/rest_master/serializers.py:3473-3477), so the
   * flag rides down in this EXISTING payload — no new endpoint.
   *
   * It is consumed ONLY by the CMS-admin permission grid (cms_admin
   * .../permissions/project/tmpl/project_item.tmpl:21,26 wraps the "editing
   * access" radio in `{{#if allow_editing}}`) and enforced server-side
   * (api/rest_cms/permissions_viewset.py:181-184, 220-223 → HTTP 400 when a
   * role is granted edit access on a non-editable tab). `fn['get.permissions']`
   * (project mvc/fn/map.js:86-92) does NOT copy it into `actualTabs`, so the
   * project board itself never reads it. Optional because dev3 has not run
   * migration 0134 yet (the column is absent → the key is absent).
   */
  allow_editing?: boolean;
  [key: string]: unknown;
}

/** CMS per-role permission row (GET /cms/1/v1.0.0/permissions/project_board_by_role/). */
export interface ICmsBoardTabRow {
  tab_id: number;
  access: boolean;
  [key: string]: unknown;
}

/**
 * Joined tab entry (fn['get.permissions'] output): row presence ⇒ tab visible,
 * `access` ⇒ editable. Sorted by master `order`.
 */
export interface IActualTab {
  name: string;
  value: string;
  order: number;
  access: boolean;
  logo: string;
}

/** User row (GET /api/v1.0/users/ list, /users/{id}, /users/?reassign_to=true). */
export interface IBoardUser {
  id: number;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  initials?: string;
  access_type?: string; // 'project' | 'network' | …
  access_type_id?: number; // 1 ⇔ 'project' (Open Question #10)
  is_cms_admin?: boolean;
  mfg_id?: number;
  /**
   * Hybrid AHU permission gate: only a 'power' user may create/edit hybrid
   * AHUs; 'standard' users get a read-only board. Prefer `effective_user_skill`
   * (server-side null-safe fallback to 'standard') when both are present.
   */
  user_skill?: 'standard' | 'power';
  effective_user_skill?: 'standard' | 'power';
  [key: string]: unknown;
}

/** Master task status (GET /master/v1.0.0/ahu/task_statuses/): 1 Not Started, 2 In Progress, 3 Complete. */
export interface ITaskStatus {
  id: number;
  name: string;
  [key: string]: unknown;
}

/** Timeline row (GET /api/v1.0/projects/{id}/timeline/). */
export interface ITimelineRow {
  id?: number;
  project_id?: number;
  task_id: number;
  status_id?: number;
  sch_start_date?: string | null;
  sch_end_date?: string | null;
  sch_started_by?: string | null;
  sch_completion_by?: string | null;
  sch_started_by_id?: number | null;
  sch_completion_by_id?: number | null;
  sch_days?: number | string | null;
  actual_start_date?: string | null;
  actual_end_date?: string | null;
  started_by?: string | null;
  completion_by?: string | null;
  started_by_id?: number | null;
  completion_by_id?: number | null;
  actual_days?: number | string | null;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Phase 3c — Sales tab (§3.9), data-layer rows 13–20.
// ---------------------------------------------------------------------------

/**
 * Sales order (GET /api/v1.0/projects/sales/{project_id} — SalesMainModel,
 * idAttribute project_id, NO trailing slash). The PATCH echo merges extra
 * keys (generate_id, set_values) into the cached object exactly like the
 * Backbone model merge — reachable via the index signature.
 */
export interface ISalesMain {
  project_id?: number;
  po_number?: string | null;
  sell_amount_to_customer?: number | string | null;
  sale_type_id?: number;
  total_rep_commission?: number | string | null;
  total_rep_commission_percent?: number | string | null;
  ordering_rep_co_commission_percent?: number | string | null;
  ordering_rep_co_commission_amount?: number | string | null;
  [key: string]: unknown;
}

/** Sale type row (GET /api/v1.0/projects/sale_types/): 1=Direct…, value e.g. 'direct'. */
export interface ISaleType {
  id: number;
  name: string;
  value: string;
  [key: string]: unknown;
}

/** Sales rep row (GET /api/v1.0/projects/sales_reps/?sales_id={pid}). */
export interface ISalesRep {
  id: number;
  sales_id?: number;
  rep_co_id?: number | null;
  rep_contact_person_id?: number | null;
  rep_co_commission_percent?: number | string | null;
  rep_co_commission_amount?: number | string | null;
  [key: string]: unknown;
}

/**
 * Sales / sales-rep PATCH response: the server echoes the PROCESSED REQUEST
 * data (NOT the serialized model) — the sent field(s) + `generate_id` +
 * `set_values` holding server-recomputed commission splits which must overlay
 * the row for display (§3.9 / risk 7; rest_project/viewset.py `process`).
 */
export interface ISalesPatchEcho {
  set_values?: Record<string, unknown>;
  generate_id?: string | null;
  [key: string]: unknown;
}

/**
 * Celery `/reports/sales_project` socket result payload: `msg.data.sales_data`
 * (price_net_actual, total_rep_commission*, …) + `msg.data.generate_id`
 * (attached to every subsequent sales/rep PATCH — risk 3 / data row 20).
 */
export interface ISalesCeleryData {
  price_net_actual?: number | string | null;
  total_rep_commission?: number | string | null;
  total_rep_commission_percent?: number | string | null;
  [key: string]: unknown;
}

/** permissions.workflow_tasks[] entry (WorkflowPopupItemView gating). */
export interface IWorkflowTaskPermission {
  workflow_task_id: number;
  start_task: boolean;
  complete_task: boolean;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Phase 3b — embedded AHU list (#main_ahus), PHASE3 inventory Part B (B1).
// ---------------------------------------------------------------------------

/**
 * AHU unit row (GET /api/v1.0/ahu/units/?ahu_list=true&include_versions=true&
 * project_id={id} — AHUUnitAHUListSerializer). Mains have
 * `parent_version_id == null`; versions carry the parent id + `version_order`.
 * Static versions have `static_version: true` + `static_version_name`.
 */
export interface IAhuUnit {
  id: number;
  project_id: number;
  tag: string;
  qty?: number | null;
  model?: string | null;
  serial?: string | null;
  dimensions?: string | null;
  tunnel_configuration?: string | null;
  sa_volume_max?: number | string | null;
  sa_volume_control?: { name?: string | null } | null;
  product_id?: number | null;
  air_system?: { name?: string | null } | null;
  ahu_main_voltage?: string | null;
  location?: string | null;
  weather_rating_id?: number | null;
  service?: string | null;
  certification?: Array<{ name?: string | null }> | null;
  remarks?: string | null;
  last_edited?: string | null;
  finished?: boolean;
  created_on?: string | null;
  static_version?: boolean;
  static_version_name?: string | null;
  parent_version_id?: number | null;
  version_order?: number | null;
  /** Hybrid/Standard choice — drives {@link ahuTunnelAddress}. */
  is_hybrid?: boolean | null;
  [key: string]: unknown;
}

/** Master lookup rows (configurations / weather ratings / cms products). */
export interface IMasterNameRow {
  id: number;
  name: string;
  [key: string]: unknown;
}

/** Tunnel row (GET /api/v1.0/ahu/tunnels/?ahu_id={id}). */
export interface IAhuTunnel {
  id: number;
  index: number | string;
  dimensions?: string | null;
  /** Hybrid AHU 3-field addressing — see {@link ahuTunnelAddress}. */
  row?: number | null;
  level?: number | null;
  position?: number | null;
  [key: string]: unknown;
}

/**
 * Hybrid AHU (KA-5220): the project board labels tunnels with 3-field
 * `row:level:position` instead of the legacy 2-field `index`.
 *
 * Only for hybrid AHUs, like the AHU board's `formatTunnelAddress`. Standard
 * AHUs keep their 2-field `index` ("1:1"), which is what the Backbone info bar
 * rendered — every tunnel carries row/level/position since the backfill, so
 * gating on those fields alone gave standard AHUs "1:1:1". A hybrid tunnel
 * that predates the backfill still falls back to its `index`.
 */
export function ahuTunnelAddress(
  tunnel: IAhuTunnel,
  isHybrid: boolean | undefined
): number | string {
  const { row, level, position } = tunnel;
  return isHybrid && row != null && level != null && position != null
    ? `${row}:${level}:${position}`
    : tunnel.index;
}

/** Section row (GET /api/v1.0/ahu/sections/?tunnel_id={id}). */
export interface IAhuSection {
  id?: number;
  name?: string;
  tag?: string | null;
  tunnel_id: number;
  [key: string]: unknown;
}

/**
 * GET /api/v1.0/users/permissions/ — array response; the Backbone view takes
 * `toJSON()[0]` and reads `.permissions.ahu_board_items` (B1.2). Cached in the
 * legacy stateStorage as 'allPermissions' (RTK Query cache = session cache).
 */
export interface IAllPermissions {
  permissions: {
    ahu_board_items: Array<{ item: string; access: boolean }>;
    /** Workflow popup per-task Start/Complete gating (parity 102). */
    workflow_tasks?: IWorkflowTaskPermission[];
    /** Role tab rows; NO 'timeline' entry ⇒ popup skips timeline reads/writes (parity 115). */
    project_board_tabs?: Array<{ tab: string; access?: boolean; [key: string]: unknown }>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/** Project row for the Duplicate popup's searchable Target Project select. */
export interface IDuplicateProjectRow {
  id: number;
  name: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Phase 3d — Costing / Pricing tabs (inventory Part B §B2/§B3), data rows
// 47–52. Both are KsdmProjectAccounting projections (rest_project viewset).
// ---------------------------------------------------------------------------

/**
 * Costing row (GET /api/v1.0/projects/costings/ — KsdmProjectCostingSerializer,
 * unfiltered list; the client finds its row by project_id). The
 * SerializerMethodFields cost_net_actual / price_raw_actual / price_list_actual
 * are ALWAYS null on GET (`def get_…: pass`) — non-null values only ever arrive
 * through the PATCH echo merge (so "actual prefers stored" is session-scoped).
 * Echo extras (generate_id / change_id / set_values) merge in via the index
 * signature, exactly like Backbone model attrs.
 */
export interface ICostingRow {
  project_id: number;
  admin_fee_actual_percent?: number | null;
  admin_fee_actual_amount?: number | null;
  inflation_contingency_actual_percent?: number | null;
  inflation_contingency_actual_amount?: number | null;
  gen_contingency_actual_percent?: number | null;
  gen_contingency_actual_amount?: number | null;
  int_sales_commission_actual_percent?: number | null;
  int_sales_commission_actual_amount?: number | null;
  int_sales_commission_sched_percent?: number | null;
  net_profit_actual_percent?: number | null;
  net_profit_actual_amount?: number | null;
  net_profit_sched_percent?: number | null;
  cost_net_actual?: number | null;
  price_raw_actual?: number | null;
  price_list_actual?: number | null;
  list_price_factor?: number | null;
  set_values?: Record<string, unknown>;
  generate_id?: string | null;
  change_id?: unknown;
  [key: string]: unknown;
}

/**
 * Pricing row (GET /api/v1.0/projects/pricings/ — KsdmProjectPricingSerializer).
 * price_actual / price_list_actual / price_net_actual are null on GET (method
 * fields); `price_sched` is NOT a serializer field — it exists on the row ONLY
 * after a PATCH echo merge (and is re-sent on every subsequent PATCH).
 */
export interface IPricingRow {
  project_id: number;
  mfg_price_multiplier?: number | null;
  price_discount_actual_percent?: number | null;
  price_discount_amount_actual?: number | null;
  freight_cost_add_actual_percent?: number | null;
  freight_cost_add_actual_amount?: number | null;
  price_actual?: number | null;
  price_list_actual?: number | null;
  price_net_actual?: number | null;
  set_values?: Record<string, unknown>;
  generate_id?: string | null;
  price_sched?: number | null;
  [key: string]: unknown;
}

/**
 * Costing / pricing PATCH response — the PROCESSED REQUEST echo from
 * `ProjectCostingViewSet.process` / `ProjectPricingViewSet.process` (pinned
 * from django/api/rest_project/viewset.py):
 *  - the edited actual-pair fields are MOVED into `set_values` (recomputed
 *    %/US$ companions; display takes precedence over the row attrs);
 *  - top-level recomputed figures ride along (costing: cost_net_actual /
 *    price_raw_actual / price_list_actual; pricing: price_sched /
 *    price_list_actual / price_actual / price_net_actual);
 *  - `generate_id` (and costing `change_id` if sent) echo back;
 *  - a PATCH with NO generate_id short-circuits server-side to `{}`.
 */
export interface IAccountingPatchEcho {
  set_values?: Record<string, unknown>;
  generate_id?: string | null;
  [key: string]: unknown;
}

/**
 * Celery `/reports/costing/project` payload: `msg.data.costing_data` (the
 * SCHEDULED column + actual fallbacks) + `msg.data.generate_id` (names the
 * server-side report JSON every costing PATCH recomputes against).
 */
export interface ICostingCeleryData {
  mtl_cost?: number | string | null;
  freight_cost_sched?: number | string | null;
  activities_cost_sched?: number | string | null;
  activities_hrs_sched?: number | string | null;
  admin_fee_sched_percent?: number | string | null;
  admin_fee_sched_amount?: number | string | null;
  inflation_contingency_sched_percent?: number | string | null;
  inflation_contingency_sched_amount?: number | string | null;
  gen_contingency_sched_percent?: number | string | null;
  gen_contingency_sched_amount?: number | string | null;
  cost_net_sched?: number | string | null;
  cost_net_actual?: number | string | null;
  int_sales_commission_sched_amount?: number | string | null;
  net_profit_sched_amount?: number | string | null;
  price_raw_sched?: number | string | null;
  price_raw_actual?: number | string | null;
  price_list_sched?: number | string | null;
  price_list_actual?: number | string | null;
  [key: string]: unknown;
}

/** Celery `/reports/pricing_project` payload: `msg.data.pricing_data`. */
export interface IPricingCeleryData {
  price_list_sched?: number | string | null;
  price_list_actual?: number | string | null;
  mfg_multiplier_sched?: number | string | null;
  price_sched?: number | string | null;
  price_actual?: number | string | null;
  freight_cost_add_sched_percent?: number | string | null;
  freight_cost_add_sched_amount?: number | string | null;
  price_net_sched?: number | string | null;
  price_net_actual?: number | string | null;
  [key: string]: unknown;
}

// ============================================================================
// Phase 3e — Shipping / Service tabs (inventory §B6/§B7).
// ============================================================================

/**
 * Shipping order row (GET /api/v1.0/projects/shippings/?project_id={id} —
 * KsdmProjectShippingSerializer, pinned from django/api/rest_project/
 * serializers.py). Dates serialize ISO `YYYY-MM-DD`; times `%H:%M`;
 * order_created_date `%Y-%m-%d %H:%M`; order_created_by is the creator's user
 * pk (PrimaryKeyRelatedField, read-only on write).
 */
export interface IShippingOrder {
  id: number;
  project_id?: number | null;
  order_number?: string | null;
  order_title?: string | null;
  order_description?: string | null;
  order_created_by?: number | null;
  order_created_date?: string | null;
  equipment_for_delivery?: string | null;
  order_note?: string | null;
  freight_co_id?: number | null;
  freight_co_contact_id?: number | null;
  freight_co_driver?: string | null;
  freight_co_driver_mob?: string | null;
  freight_co_driver_email?: string | null;
  type_qty_trucks?: string | null;
  deliver_to?: string | null;
  delivery_delivery_address_line_1?: string | null;
  delivery_delivery_address_line_2?: string | null;
  delivery_address_city?: string | null;
  delivery_address_st?: string | null;
  delivery_address_postal_code?: string | null;
  delivery_address_country?: string | null;
  delivery_co_contact_person?: string | null;
  delivery_contact_tel_office?: string | null;
  delivery_contact_tel_mobile?: string | null;
  delivery_contact_email?: string | null;
  instructions?: string | null;
  sched_loading_date?: string | null;
  sched_loading_time_start?: string | null;
  sched_loading_time_complete?: string | null;
  actual_loading_date?: string | null;
  actual_loading_time_start?: string | null;
  actual_loading_time_complete?: string | null;
  sched_ship_date?: string | null;
  sched_ship_time?: string | null;
  actual_ship_date?: string | null;
  actual_ship_time?: string | null;
  sched_delivery_date?: string | null;
  sched_delivery_time?: string | null;
  actual_delivery_date?: string | null;
  actual_delivery_time?: string | null;
  sched_off_load_date?: string | null;
  sched_off_load_time_start?: string | null;
  sched_off_load_time_complete?: string | null;
  actual_off_load_date?: string | null;
  actual_off_load_time_start?: string | null;
  actual_off_load_time_complete?: string | null;
  [key: string]: unknown;
}

/**
 * Service order row (GET /api/v1.0/projects/services/?project_id={id} —
 * KsdmProjectServiceSerializer). The numeric fields can hold `false` locally
 * (the legacy validateNumberField invalid-saves-false quirk, §B7) and `NaN`
 * (decimal garbage — JSON-serializes to null); service times serialize
 * `HH:MM:SS` (TimeField without format).
 */
export interface IServiceOrder {
  id: number;
  project_id?: number | null;
  order_number?: string | null;
  order_title?: string | null;
  order_description?: string | null;
  order_created_by?: number | null;
  order_created_date?: string | null;
  equipment_for_service?: string | null;
  order_note?: string | null;
  service_co_id?: number | null;
  service_co_contact_id?: number | null;
  service_tech?: string | null;
  service_tech_mob?: string | null;
  service_tech_tel_office?: string | null;
  service_tech_email?: string | null;
  service_address_line_1?: string | null;
  service_address_line_2?: string | null;
  service_address_city?: string | null;
  service_address_st?: string | null;
  service_address_postal_code?: string | null;
  service_address_country?: string | null;
  service_co_contact_person?: string | null;
  service_contact_tel_office?: string | null;
  service_contact_tel_mobile?: string | null;
  service_contact_email?: string | null;
  sched_service_start_date?: string | null;
  sched_service_start_time?: string | null;
  sched_service_complete_date?: string | null;
  sched_service_complete_time?: string | null;
  actual_service_start_date?: string | null;
  actual_service_start_time?: string | null;
  actual_service_complete_date?: string | null;
  actual_service_complete_time?: string | null;
  sched_cost?: number | boolean | null;
  actual_cost?: number | boolean | null;
  sched_total_time?: number | boolean | null;
  sched_total_trips?: number | boolean | null;
  actual_total_time?: number | boolean | null;
  actual_total_trips?: number | boolean | null;
  instructions?: string | null;
  [key: string]: unknown;
}

/**
 * AHU Components → Coils summary row
 * (GET /api/v1.0/ahu/coils/project/{project_id} — `AHUCoilProjectSerializer`,
 * api/rest_ahu/coil_serializers.py:300-337; KA-5258 / PR #798).
 *
 * Every field except `id`/`section_id`/`ahu_id` is a resolved display string
 * and every one of them is null-safe on the server: `type`/`hand` come from
 * the SELECTED master coil (`master_coil.type.name` / `master_coil.conn_hand.name`)
 * and stay absent until a coil has actually been picked, `duty` from
 * `duty.name`, `section` from `section.tag or section.name`. DRF omits nothing
 * — SerializerMethodFields that fall through their `if` return Python `None`,
 * i.e. JSON `null`.
 */
export interface IAhuCoilProjectRow {
  id: number;
  section_id: number;
  /** AHU tag (`section.tunnel.ahu.tag`) — the rowspan'd group label. */
  ahu: string | null;
  /** AHU pk (`section.tunnel.ahu.id`) — the grouping key. */
  ahu_id: number | null;
  /** Section tag, falling back to the section name. */
  section: string | null;
  type: string | null;
  duty: string | null;
  hand: string | null;
  [key: string]: unknown;
}

/**
 * AHU Accessories → Access Doors summary row
 * (GET /api/v1.0/ahu/access_doors/project/{project_id} —
 * `AHUAccessoryAccessDoorProjectSerializer`,
 * api/rest_ahu/access_door_serializers.py:77-103). The endpoint pre-dates
 * KA-5258; PR #798 only added `swing` (`swing.name`) to it.
 *
 * `width`/`height` are DRF `DecimalField(max_digits=15, decimal_places=2)` over
 * `double precision` columns; this install has COERCE_DECIMAL_TO_STRING off, so
 * they arrive as JSON NUMBERS (observed on dev3: `"width":30.0`). Typed
 * `string | number` because the setting is per-install and `formatRough`
 * handles both identically (`Number(v).toFixed(2)`).
 */
export interface IAhuAccessDoorProjectRow {
  id: number;
  section_id: number;
  ahu: string | null;
  ahu_id: number | null;
  section: string | null;
  mounting_plane: string | null;
  swing: string | null;
  hinge_side: string | null;
  width: string | number | null;
  height: string | number | null;
  [key: string]: unknown;
}
